import * as Sentry from '@sentry/react';
import { useMutation } from '@do/graphql-client';
import { LoadingState, style } from '@do/walrus';
import { Form, Formik } from 'formik';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { PUBLIC_ENDPOINT } from '../constants';
import copy from '../copy';
import { sendSms2fa, sendSms2faVariables } from '../generated-types/sendSms2fa';
import { LoginStatus, TwoFactorAuthType } from '../interfaces/login';
import { SEND_SMS, TWO_FACTOR } from '../queries/queries';
import { addSessionData, handleFormError, saveSessionData } from '../utils/gql-utils';
import {
  validationCopy,
  yupVerificationCharValidationFn,
  yupVerificationNumberValidation,
} from '../utils/validation-utils';
import ClickLink from './ClickLink';
import DOLink from './DOLink';
import ErrorBanner from './ErrorBanner';
import SpinnerButton from './SpinnerButton';
import Text from './Text';
import TextInput from './TextInput';
import Title from './Title';

const Spinner = styled(LoadingState)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  top: -1px;
  margin: 0 ${style.vars.space['1']};
`;

const copy2fa = copy.form.two_factor_auth;

const VALIDATION_2FA = {
  authenticator: Yup.object().shape({
    code: yupVerificationNumberValidation,
  }),
  sms: Yup.object().shape({
    code: yupVerificationCharValidationFn(),
  }),
  recovery_codes: Yup.object().shape({
    code: yupVerificationCharValidationFn(true),
  }),
};

const TwoFactorSmsHeader: React.FC = () => {
  const [sendSms, { loading: isSendingSMS }] = useMutation<sendSms2fa, sendSms2faVariables>(SEND_SMS, {
    context: {
      url: PUBLIC_ENDPOINT,
    },
    onError: (error) => {
      Sentry.captureException(error);
      console.error('Error in sending sms:', error);
    },
  });

  return (
    <Text className="do-u-mt--medium">
      {copy2fa.sms.details}
      <br />
      <ClickLink
        disabled={isSendingSMS}
        onClick={() =>
          sendSms({
            variables: {
              sendSms2faRequest: addSessionData(),
            },
          })
        }
      >
        {copy2fa.sms.resend_link}
      </ClickLink>
      {isSendingSMS && <Spinner />}
    </Text>
  );
};

interface Props {
  type: TwoFactorAuthType;
  nextStepType?: TwoFactorAuthType;
  changeStep: () => void;
  isSecondary?: boolean;
}

const TwoFactorFooter: React.FC<Props> = ({ type, nextStepType, changeStep, isSecondary = false }) => {
  const [sendSms, { loading: isSendingSMS }] = useMutation<sendSms2fa, sendSms2faVariables>(SEND_SMS, {
    context: {
      url: PUBLIC_ENDPOINT,
    },
  });
  let nextStepCopy = '';
  if (nextStepType) {
    const subCopy = copy2fa[nextStepType];
    if (subCopy) {
      if (isSecondary) {
        nextStepCopy = subCopy.forward_link;
      } else {
        nextStepCopy = subCopy.backup_link;
      }
    }
  }

  const hasNextPage = isSecondary || nextStepType;
  return (
    <>
      <Text className="do-u-mt--medium do-u-mb--small need-help-text">{copy2fa[type].no_access}</Text>
      {hasNextPage && (
        <ClickLink
          disabled={isSendingSMS}
          onClick={() => {
            if (nextStepType === 'sms') {
              return sendSms({
                variables: {
                  sendSms2faRequest: addSessionData(),
                },
              }).finally(changeStep);
            }
            changeStep();
          }}
        >
          {nextStepCopy}
        </ClickLink>
      )}
      {isSendingSMS && <Spinner />}
      {hasNextPage && <> {copy.form.or} </>}
      <DOLink path={'/company/contact/support'}>
        {copy.form.contact_support_link.replace(/./, (str: string) => (hasNextPage ? str : str.toUpperCase()))}
      </DOLink>
    </>
  );
};

interface TwoFactorGqlResponse {
  request_id: string;
  twoFactorVerification: {
    status: LoginStatus;
    session_data: string;
    redirect_url: string;
  };
}

interface TwoFactorGqlVariables {
  twoFactorRequest: {
    type: TwoFactorAuthType;
    code: string;
    session_data?: string | null;
  };
}

interface ErrorMessage {
  title?: string;
  details: string;
  requestId?: string;
}

const TwoFactorAuthForm: React.FC<Props> = (props) => {
  const { type, isSecondary = false } = props;
  const [genericServerError, setGenericServerError] = React.useState<ErrorMessage | null>(null);
  const [twoFactor, { loading: isSendingSMS }] = useMutation<TwoFactorGqlResponse, TwoFactorGqlVariables>(TWO_FACTOR, {
    context: { url: PUBLIC_ENDPOINT },
  });

  return (
    <>
      <Title title={copy2fa[isSecondary ? 'backup_title' : 'title']} />
      {type === 'sms' ? <TwoFactorSmsHeader /> : <Text className="do-u-mt--medium">{copy2fa[type].details}</Text>}

      <Formik
        initialValues={{
          code: '',
        }}
        validationSchema={VALIDATION_2FA[type]}
        onSubmit={async (values, { setErrors }) => {
          setGenericServerError(null); /* Reset displayed error */

          return twoFactor({
            variables: {
              twoFactorRequest: addSessionData({ type, ...values }),
            },
          })
            .then((successResponse) => {
              const { status, session_data: sessionData } = successResponse.data?.twoFactorVerification || {};
              const requestId = successResponse.data?.request_id;

              saveSessionData(sessionData);

              if (status === 'granted') {
                window.location.reload();
              } else if (status === 'rejected') {
                setGenericServerError({
                  title: copy.errors.incorrect_two_factor.title,
                  details: copy.errors.incorrect_two_factor.details,
                  requestId,
                });
              }
            }, handleFormError(setErrors, setGenericServerError))
            .catch((err) => {
              Sentry.captureException(err);
              console.error(err);

              const requestId = (err as { extensions?: { request_id?: string } })?.extensions?.request_id;

              setGenericServerError({
                title: copy.errors.unknown.title,
                details: copy.errors.unknown.details,
                requestId,
              });
            });
        }}
      >
        {({ errors, values, touched, setFieldValue, isSubmitting }) => (
          <Form className="do-u-pb--small">
            {genericServerError && <ErrorBanner error={genericServerError} />}

            <TextInput
              className="do-u-mt--medium"
              name="code"
              placeholder={`${copy2fa.placeholder_prefix} ${validationCopy.two_factor_auth[type].code_label}`}
              label={validationCopy.two_factor_auth[type].code_label}
              autoComplete="one-time-code"
              touched={touched.code}
              error={errors.code}
              value={values.code}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onChange={(e: React.FormEvent<HTMLInputElement>) => {
                setFieldValue('code', e.currentTarget?.value?.trim());
              }}
            />
            <SpinnerButton
              type="submit"
              fullWidth
              spinning={isSendingSMS || isSubmitting}
              disabled={isSendingSMS || isSubmitting}
              loadingMessage={copy.loading.login.belowButton}
            >
              {copy2fa.button_text}
            </SpinnerButton>

            <TwoFactorFooter {...props} />
          </Form>
        )}
      </Formik>
    </>
  );
};

export default TwoFactorAuthForm;
